import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { getMilestone } from '@/lib/points';
import { MILESTONE_TIERS } from '@/lib/milestones';
import MilestoneEmblem from '@/components/MilestoneEmblem';

const MilestonePanel: React.FC<{ userIdentifier: string }> = ({
  userIdentifier,
}) => {
  const { t } = useTranslation();
  const { token } = useAuth();
  const { data, isLoading } = useQuery({
    queryKey: ['points', 'milestone', userIdentifier],
    queryFn: () => getMilestone(token as string, userIdentifier),
    enabled: !!token && !!userIdentifier,
  });

  const [open, setOpen] = useState<number | null>(null);
  useEffect(() => {
    if (open === null) return;
    const close = () => setOpen(null);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [open]);

  const hoverable =
    typeof window !== 'undefined' &&
    !!window.matchMedia &&
    window.matchMedia('(hover: hover)').matches;

  if (isLoading || !data || data.current_level === undefined) return null;
  const current = data.current_level;

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-4">
      <div className="flex items-center gap-3 bg-slate-50 rounded-xl p-3 mb-4">
        <MilestoneEmblem level={current} size={58} state="current" />
        <div>
          <p className="text-[15px] font-semibold text-slate-800">
            {data.current_title}
            <span className="ml-1 text-xs font-medium text-blue-700 bg-blue-50 rounded-full px-2 py-0.5">
              L{current}
            </span>
          </p>
          {data.next_level !== null && (
            <p className="text-xs text-slate-500 mt-0.5">
              Next: {data.next_name}
              {t('common.l')}
              {data.next_level}
            </p>
          )}
        </div>
      </div>

      <p className="text-xs text-slate-500 mb-3">{t('common.your.journey')}</p>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-y-6 gap-x-2.5">
        {MILESTONE_TIERS.map((tier) => {
          const state =
            tier.level === current
              ? 'current'
              : tier.level < current
                ? 'earned'
                : 'locked';
          const isNextLocked = tier.level === current + 1;
          const showBars = isNextLocked && data.criteria.length > 0;
          const prevName =
            tier.level > 0 ? MILESTONE_TIERS[tier.level - 1].name : '';
          return (
            <div
              key={tier.level}
              className="relative flex flex-col items-center"
            >
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  if (!hoverable)
                    setOpen(open === tier.level ? null : tier.level);
                }}
                onMouseEnter={() => hoverable && setOpen(tier.level)}
                onMouseLeave={() => hoverable && setOpen(null)}
                style={{ WebkitTapHighlightColor: 'transparent' }}
                className="flex flex-col items-center gap-1.5 outline-none focus:outline-none focus-visible:outline-none"
              >
                <MilestoneEmblem level={tier.level} size={56} state={state} />
                <span
                  className={`text-[13px] font-medium text-center leading-tight ${state === 'locked' ? 'text-slate-400' : 'text-slate-800'}`}
                >
                  {tier.name}
                </span>
                <span className="text-[11px] text-slate-400">
                  L{tier.level} ·{' '}
                  {state === 'current'
                    ? 'Current'
                    : state === 'earned'
                      ? 'Earned'
                      : 'Locked'}
                </span>
              </button>

              {open === tier.level && (
                <div
                  onClick={(e) => e.stopPropagation()}
                  className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 z-20 w-56"
                >
                  <div className="bg-white border border-slate-200 rounded-xl shadow-lg p-3 text-left">
                    <p className="text-[12px] font-semibold text-slate-800 mb-1.5">
                      {tier.name}
                    </p>
                    {(state === 'earned' || state === 'current') && (
                      <p className="text-[11px] text-emerald-700">
                        {t('common.milestone.earned')}
                        {state === 'current'
                          ? ' — your current milestone.'
                          : '.'}
                      </p>
                    )}
                    {showBars && (
                      <>
                        <p className="text-[11px] text-slate-500 mb-1.5">
                          {t('ui.what.you.need.to.unlock')} {tier.name}
                        </p>
                        <div className="grid gap-1">
                          {data.criteria.map((c) => (
                            <div
                              key={c.key}
                              className="flex items-center gap-1.5"
                            >
                              <span className="w-[58px] text-[10px] text-slate-600 truncate">
                                {c.label}
                              </span>
                              <span className="flex-1 h-1 rounded-full bg-emerald-100 overflow-hidden">
                                <span
                                  className="block h-full rounded-full bg-emerald-600"
                                  style={{
                                    width: `${c.target <= 0 ? 100 : Math.min(100, Math.round((c.current / c.target) * 100))}%`,
                                  }}
                                />
                              </span>
                              <span className="text-[10px] text-slate-500 w-[42px] text-right">
                                {Math.floor(c.current)}/{Math.round(c.target)}
                              </span>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                    {state === 'locked' && !isNextLocked && (
                      <p className="text-[11px] text-slate-600">
                        Reach{' '}
                        <span className="font-medium text-slate-800">
                          {prevName}
                        </span>{' '}
                        {t('ui.first.to.see.what.this.milestone.needs')}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default MilestonePanel;
